import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Divider,
  Collapse,
  Chip,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect, useMemo, useState } from "react";
import { startToast, updateToastError, updateToastSuccess } from "../../../utils/notification-loading";
import API from "../../../api";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#449095" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#449095" },
};

// ── Spec-mandated enums ─────────────────────────────────────────────────────
const FORMATS   = ["CEF", "LEEF", "JSON"];
const PROTOCOLS = ["UDP", "TCP"];

// ── Validators ──────────────────────────────────────────────────────────────
const isValidPort = (p) => {
  const n = Number(p);
  return Number.isInteger(n) && n >= 1 && n <= 65535;
};
const isValidPEM = (cert) => {
  if (!cert?.trim()) return true;       // optional
  const t = cert.trim();
  return t.includes("-----BEGIN CERTIFICATE-----") && t.includes("-----END CERTIFICATE-----");
};
const isEncryptedPrivateKey = (pem) =>
  !!pem && /-----BEGIN ENCRYPTED PRIVATE KEY-----/.test(pem);

// ── Format reference panel ──────────────────────────────────────────────────
const FormatReference = ({ activeFormat, sampleEvents, formatInfo, protocolExtras }) => {
  const [eventKey, setEventKey] = useState("security");
  const [copied, setCopied] = useState(null);

  const ev = sampleEvents[eventKey] ?? sampleEvents[Object.keys(sampleEvents)[0]];
  const sample = useMemo(() => {
    if (!ev) return "";
    if (activeFormat === "JSON") return JSON.stringify(ev.json, null, 2);
    if (activeFormat === "CEF")  return ev.cef;
    return ev.leef;
  }, [activeFormat, ev]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sample);
      setCopied(eventKey);
      setTimeout(() => setCopied(null), 1200);
    } catch {}
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: "rgba(69,144,149,0.05)",
        border: "1px solid rgba(69,144,149,0.18)",
        borderRadius: 2,
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#aaa" }} />}
        sx={{ "& .MuiAccordionSummary-content": { my: 1.25 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#449095" }} />
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#e8e8e8" }}>
            What does the device send?
          </Typography>
          <Chip
            size="small"
            label={activeFormat}
            sx={{
              height: 18, fontSize: "0.62rem", fontWeight: 700, letterSpacing: 0.3,
              backgroundColor: "rgba(69,144,149,0.18)",
              color: "#43B4B9",
              border: "1px solid rgba(69,144,149,0.3)",
            }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
        <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 1.5 }}>
          {formatInfo[activeFormat]}
        </Typography>

        <Tabs
          value={eventKey}
          onChange={(_, v) => setEventKey(v)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="inherit"
          TabIndicatorProps={{ sx: { backgroundColor: "#449095", height: 2 } }}
          sx={{
            minHeight: 32,
            mb: 1.2,
            "& .MuiTab-root": {
              minHeight: 32,
              fontSize: "0.7rem",
              textTransform: "none",
              color: "#888",
              fontWeight: 600,
              px: 1.25,
              "&.Mui-selected": { color: "#43B4B9" },
            },
          }}
        >
          {Object.entries(sampleEvents).map(([k, v]) => (
            <Tab key={k} value={k} label={v.label} />
          ))}
        </Tabs>

        <Box
          sx={{
            position: "relative",
            mb: 1.5,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 1.5,
            backgroundColor: "rgba(0,0,0,0.35)",
          }}
        >
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              fontSize: "0.72rem",
              lineHeight: 1.45,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              color: "#e0e0e0",
              overflowX: "auto",
              whiteSpace: "pre",
              maxHeight: 220,
            }}
          >
            {sample}
          </Box>
          <Tooltip title={copied === eventKey ? "Copied" : "Copy sample"}>
            <IconButton
              size="small"
              onClick={copy}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: copied === eventKey ? "#4cceac" : "#888",
                backgroundColor: "rgba(0,0,0,0.4)",
                "&:hover": { color: "#43B4B9", backgroundColor: "rgba(69,144,149,0.15)" },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, fontSize: "0.65rem" }}>
            Key fields
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            columnGap: 1.5,
            rowGap: 0.4,
            fontSize: "0.74rem",
          }}
        >
          {(ev.fields ?? []).map(([field, desc]) => (
            <Box key={field} sx={{ display: "contents" }}>
              <Box component="code" sx={{
                fontFamily: "monospace", color: "#43B4B9", fontWeight: 600, whiteSpace: "nowrap",
              }}>
                {field}
              </Box>
              <Box sx={{ color: "#bbb" }}>{desc}</Box>
            </Box>
          ))}
        </Box>

        {ev.extras === "byAppProto" && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, fontSize: "0.65rem" }}>
                Protocol-specific extras
              </Typography>
              <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 0.3, fontSize: "0.7rem" }}>
                When <code style={{ fontFamily: "monospace", color: "#43B4B9" }}>app_proto</code> identifies a known L7 protocol,
                the event carries an additional nested object with that name. Below the most common ones.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.entries(protocolExtras).map(([key, info]) => (
                <Accordion
                  key={key}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.025)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    borderRadius: 1,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#999", fontSize: 16 }} />}
                    sx={{ minHeight: 32, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        size="small"
                        label={`app_proto = ${key}`}
                        sx={{
                          height: 18, fontSize: "0.6rem", fontWeight: 700,
                          backgroundColor: "rgba(67,180,185,0.15)",
                          color: "#43B4B9",
                          border: "1px solid rgba(67,180,185,0.3)",
                          fontFamily: "monospace",
                        }}
                      />
                      <Typography sx={{ fontSize: "0.75rem", color: "#ddd", fontWeight: 600 }}>
                        {info.label}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 1.5, px: 1.5 }}>
                    <Box sx={{
                      display: "grid",
                      gridTemplateColumns: "max-content 1fr",
                      columnGap: 1.5,
                      rowGap: 0.35,
                      fontSize: "0.72rem",
                      mb: 1,
                    }}>
                      {(info.fields ?? []).map(([field, desc]) => (
                        <Box key={field} sx={{ display: "contents" }}>
                          <Box component="code" sx={{
                            fontFamily: "monospace", color: "#43B4B9", fontWeight: 600, whiteSpace: "nowrap",
                          }}>
                            {field}
                          </Box>
                          <Box sx={{ color: "#bbb" }}>{desc}</Box>
                        </Box>
                      ))}
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        m: 0, p: 1, borderRadius: 1,
                        backgroundColor: "rgba(0,0,0,0.35)",
                        border: "1px solid rgba(255,255,255,0.05)",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        fontSize: "0.68rem",
                        lineHeight: 1.45,
                        color: "#e0e0e0",
                        overflowX: "auto",
                        whiteSpace: "pre",
                      }}
                    >
                      {JSON.stringify(info.sample, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}

        {ev.note && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 1.5,
              border: "1px solid rgba(255,210,127,0.25)",
              backgroundColor: "rgba(255,210,127,0.06)",
              fontSize: "0.74rem",
              lineHeight: 1.5,
              color: "#d4c89a",
              "& code": {
                fontFamily: "monospace",
                color: "#FFD27F",
                backgroundColor: "rgba(255,210,127,0.12)",
                px: 0.5,
                borderRadius: 0.5,
              },
            }}
          >
            {ev.note}
          </Box>
        )}

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "#666", mr: 0.5, alignSelf: "center" }}>
            Emitted when these topics are enabled:
          </Typography>
          {(ev.topics ?? []).map((tk) => (
            <Chip
              key={tk}
              size="small"
              label={tk}
              sx={{
                height: 18,
                fontSize: "0.62rem",
                fontWeight: 600,
                backgroundColor: "rgba(255,255,255,0.05)",
                color: "#aaa",
                border: "1px solid #333",
              }}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// ════════════════════════════════════════════════════════════════════════════
const SyslogConfig = ({ storedSyslog }) => {
  const [cfg, setCfg] = useState({
    host: "",
    port: "",
    format: "CEF",
    protocol: "UDP",
    hostname: "",
    topics: [],
    tls: false,
    caCert: "",
    clientCert: "",
    verifyHostname: true,
  });

  const [errors, setErrors] = useState({
    host: false, port: false, caCert: false, clientCert: false, topics: false,
  });

  const [refDoc, setRefDoc] = useState(null);

  const set = (key, val) => setCfg((prev) => ({ ...prev, [key]: val }));
  const setErr = (key, val) => setErrors((prev) => ({ ...prev, [key]: val }));

  // ── Hydrate from server-stored values ────────────────────────────────────
  useEffect(() => {
    if (!storedSyslog || Object.keys(storedSyslog).length === 0) return;
    const rawProto = (storedSyslog.protocol || "UDP").toUpperCase();
    const isTLSProto = rawProto === "TLS" || (storedSyslog.tls && rawProto === "TCP");
    const incoming = {
      host:           storedSyslog.host || storedSyslog.ip || "",
      port:           storedSyslog.port || "",
      format:         (storedSyslog.format || storedSyslog.type || "CEF").toUpperCase(),
      protocol:       isTLSProto ? "TCP" : rawProto,
      hostname:       storedSyslog.hostname || "",
      topics:         Array.isArray(storedSyslog.topics) && storedSyslog.topics.length > 0
                        ? storedSyslog.topics
                        : [],
      tls:            isTLSProto,
      caCert:         storedSyslog.tls_ca_cert_content || storedSyslog.certificate || "",
      clientCert:     storedSyslog.tls_client_cert_content || "",
      verifyHostname: storedSyslog.tls_verify_hostname ?? true,
    };
    setCfg(incoming);
  }, [storedSyslog]);

  useEffect(() => {
    API.getSystemConfig("syslog", "reference").then((res) => {
      const doc = res.data?.[0]?.value;
      if (doc) setRefDoc(doc);
    }).catch(() => {});
  }, []);

  const allTopics      = refDoc?.topics          ?? [];
  const sampleEvents   = refDoc?.sample_events   ?? {};
  const formatInfo     = refDoc?.format_info     ?? {};
  const protocolExtras = refDoc?.protocol_extras ?? {};

  // Imposta i topic di default (non opt-in) appena arriva il documento,
  // solo se non c'è già una configurazione salvata sul device.
  useEffect(() => {
    if (!refDoc?.topics) return;
    setCfg((prev) => {
      if (prev.topics.length > 0) return prev;
      const defaults = refDoc.topics.filter((t) => !t.optIn).map((t) => t.key);
      return { ...prev, topics: defaults };
    });
  }, [refDoc]);

  // ── Topic toggling ───────────────────────────────────────────────────────
  const toggleTopic = (key) => {
    setErr("topics", false);
    setCfg((prev) => ({
      ...prev,
      topics: prev.topics.includes(key)
        ? prev.topics.filter((k) => k !== key)
        : [...prev.topics, key],
    }));
  };

  const isTLS = cfg.protocol === "TCP" && cfg.tls;

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = () => {
    startToast();

    // ── Validate ────────────────────────────────────────────────
    const newErrs = { host: false, port: false, caCert: false, clientCert: false, topics: false };
    if (!cfg.host.trim()) newErrs.host = true;
    if (!isValidPort(cfg.port)) newErrs.port = true;
    if (isTLS) {
      if (!isValidPEM(cfg.caCert)) newErrs.caCert = true;
      if (cfg.clientCert?.trim()) {
        if (!isValidPEM(cfg.clientCert)) newErrs.clientCert = true;
        if (isEncryptedPrivateKey(cfg.clientCert)) newErrs.clientCert = true;
      }
    }
    if (cfg.topics.length === 0) newErrs.topics = true;  // require at least 1 explicit topic
    setErrors(newErrs);

    if (Object.values(newErrs).some(Boolean)) {
      const reasons = [];
      if (newErrs.host)       reasons.push("host is required");
      if (newErrs.port)       reasons.push("port must be 1–65535");
      if (newErrs.caCert)     reasons.push("CA certificate is not a valid PEM");
      if (newErrs.clientCert) reasons.push("client certificate invalid (or has an encrypted private key)");
      if (newErrs.topics)     reasons.push("select at least one topic");
      updateToastError(`Cannot save: ${reasons.join("; ")}`);
      return;
    }

    // ── Build payload exactly as per spec ───────────────────────
    const payload = {
      host:     cfg.host.trim(),
      port:     Number(cfg.port),
      format:   cfg.format,
      protocol: isTLS ? "TLS" : cfg.protocol,
      topics:   cfg.topics,
    };
    if (cfg.hostname.trim()) payload.hostname = cfg.hostname.trim();
    if (isTLS) {
      payload.tls_verify_hostname = !!cfg.verifyHostname;
      if (cfg.caCert.trim())     payload.tls_ca_cert_content     = cfg.caCert.trim();
      if (cfg.clientCert.trim()) payload.tls_client_cert_content = cfg.clientCert.trim();
    }

    API.setSyslog(payload)
      .then(() => updateToastSuccess("Syslog settings sent to device."))
      .catch(() => updateToastError("Failed to publish syslog settings."));
  };

  // ── Reset (send {action:"reset"} per spec) ───────────────────────────────
  const handleReset = () => {
    startToast();
    API.resetSyslog({ action: "reset" })
      .then(() => {
        setCfg({
          host: "", port: "", format: "CEF", protocol: "UDP", hostname: "",
          topics: allTopics.filter((t) => !t.optIn).map((t) => t.key), caCert: "", clientCert: "",
          verifyHostname: true,
        });
        setErrors({ host: false, port: false, caCert: false, clientCert: false, topics: false });
        updateToastSuccess("Syslog collector configuration cleared.");
      })
      .catch(() => updateToastError("Failed to reset syslog settings."));
  };

  // ── Pretty topic chip with description tooltip ───────────────────────────
  const TopicChip = ({ t }) => {
    const active = cfg.topics.includes(t.key);
    const tip = (
      <Box sx={{ maxWidth: 280 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 0.25 }}>
          {t.label} {t.optIn && <span style={{ color: "#FFD27F" }}>(opt-in)</span>}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", opacity: 0.85, lineHeight: 1.4 }}>
          {t.description}
        </Typography>
      </Box>
    );
    return (
      <Tooltip title={tip} arrow placement="top">
        <Chip
          label={t.label + (t.optIn ? " ★" : "")}
          size="small"
          onClick={() => toggleTopic(t.key)}
          sx={{
            cursor: "pointer",
            fontWeight: 700,
            fontSize: "0.72rem",
            backgroundColor: active ? `${t.color}22` : "transparent",
            color:           active ? t.color : "#555",
            border:          `1px solid ${active ? t.color : "#333"}`,
            "&:hover": {
              backgroundColor: `${t.color}33`,
              color: t.color,
              borderColor: t.color,
            },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* ── Header ────────────────────────────────────────────── */}
      <Box>
        <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>Syslog forwarder</Typography>
        <Typography variant="caption" color="text.secondary">
          Forward events directly from the LECS sensor to a SIEM / log collector.
          The sensor sends logs over the local network without passing through the cloud.
        </Typography>
      </Box>

      <Divider />

      {/* ── Destination ───────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Destination
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            size="small" label="Host" required
            value={cfg.host}
            onChange={(e) => { set("host", e.target.value); setErr("host", false); }}
            error={errors.host}
            helperText={errors.host ? "Required" : ""}
            sx={{ flex: 1, minWidth: 200 }}
            placeholder="siem.example.com or 10.0.0.5"
          />
          <TextField
            size="small" label="Port" required type="number"
            value={cfg.port}
            onChange={(e) => { set("port", e.target.value); setErr("port", false); }}
            error={errors.port}
            helperText={errors.port ? "1–65535" : ""}
            sx={{ width: 120 }}
            inputProps={{ min: 1, max: 65535 }}
          />
          <TextField
            select size="small" label="Protocol"
            value={cfg.protocol}
            onChange={(e) => { set("protocol", e.target.value); if (e.target.value !== "TCP") set("tls", false); }}
            sx={{ width: 120 }}
          >
            {PROTOCOLS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          {cfg.protocol === "TCP" && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={cfg.tls}
                  onChange={(e) => set("tls", e.target.checked)}
                  sx={{ ml: 0.5 }}
                />
              }
              label={<Typography variant="caption" sx={{ color: cfg.tls ? "#43B4B9" : "#888" }}>TLS</Typography>}
              sx={{ ml: 0.5, mr: 0 }}
            />
          )}
          <TextField
            select size="small" label="Format" required
            value={cfg.format}
            onChange={(e) => set("format", e.target.value)}
            sx={{ width: 120 }}
          >
            {FORMATS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField
          size="small" label="Hostname (optional)"
          value={cfg.hostname}
          onChange={(e) => set("hostname", e.target.value)}
          placeholder="Override sensor's matricola/serial in messages"
          sx={{ mt: 1.5, maxWidth: 360 }}
          fullWidth
        />
      </Box>

      {/* ── Format reference (live sample per selected format) ─ */}
      {Object.keys(sampleEvents).length > 0 && (
        <FormatReference
          activeFormat={cfg.format}
          sampleEvents={sampleEvents}
          formatInfo={formatInfo}
          protocolExtras={protocolExtras}
        />
      )}

      {/* ── Topics ────────────────────────────────────────────── */}
      <Box>
        <Box display="flex" alignItems="baseline" gap={1} mb={1.5}>
          <Typography variant="subtitle2" sx={{ color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
            Topics
          </Typography>
          <Typography variant="caption" sx={{ color: "#666" }}>
            Categories to forward. ★ = opt-in (off by default on the device).
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {allTopics.map((t) => <TopicChip key={t.key} t={t} />)}
        </Box>
        {errors.topics && (
          <Typography variant="caption" sx={{ color: "#ff6b6b", mt: 1, display: "block" }}>
            Select at least one topic.
          </Typography>
        )}
      </Box>

      {/* ── TLS (visible only when protocol = TLS) ────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          TLS
        </Typography>
        <Collapse in={isTLS} timeout={300}>
          <Box sx={{
            mt: 1.5, p: 2, display: "flex", flexDirection: "column", gap: 2,
            backgroundColor: "rgba(69,144,149,0.05)", borderRadius: 2,
            border: "1px solid rgba(69,144,149,0.2)",
          }}>
            <TextField
              size="small" label="CA certificate (PEM)" multiline rows={6}
              value={cfg.caCert}
              onChange={(e) => { set("caCert", e.target.value); setErr("caCert", false); }}
              placeholder="-----BEGIN CERTIFICATE-----"
              error={errors.caCert}
              helperText={
                errors.caCert
                  ? "Invalid PEM. Must include BEGIN/END CERTIFICATE markers."
                  : "Used to verify the SIEM's server certificate"
              }
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.8rem" } }}
            />
            <TextField
              size="small" label="Client certificate + private key (PEM)" multiline rows={8}
              value={cfg.clientCert}
              onChange={(e) => { set("clientCert", e.target.value); setErr("clientCert", false); }}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----&#10;-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              error={errors.clientCert}
              helperText={
                errors.clientCert
                  ? "Invalid PEM or encrypted private key (must be unencrypted)."
                  : "Optional. Encrypted keys (BEGIN ENCRYPTED PRIVATE KEY) are rejected by the device."
              }
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.8rem" } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={cfg.verifyHostname}
                  onChange={(e) => set("verifyHostname", e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Verify server hostname"
            />
          </Box>
        </Collapse>
      </Box>

      {/* ── Actions ───────────────────────────────────────────── */}
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained" onClick={handleSave}
            sx={{ backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" }, px: 3 }}
          >
            Save
          </Button>
          <Button
            variant="contained" onClick={handleReset} startIcon={<DeleteIcon />}
            sx={{ backgroundColor: "#c0392b", "&:hover": { backgroundColor: "#a93226" }, px: 3 }}
          >
            Reset
          </Button>
        </Stack>
      </Box>

    </Box>
  );
};

export default SyslogConfig;
